import { addDays, phaseWeekNumber, startOfCalendarWeek } from '~/calculations/calendar'
import { calculateWeightTrend } from '~/calculations/weightTrends'
import type { MeasurementSessionRecord } from '~/models/measurementSession'
import { authorityOf } from '~/models/nutritionPlan'
import type { RulesOutcome } from '~/models/rulesOpinion'
import type { WeeklyReviewRecord } from '~/models/weeklyReview'
import { evaluateWeek } from '~/rules/engine'
import type {
  NutritionContext,
  RulesInputSnapshot,
  WaistObservation,
} from '~/rules/inputs/snapshot'
import type { AdherenceEvidence } from '~/rules/prioritize'
import { classifyAdherence } from '~/rules/prioritize'
import { DEFAULT_RULES_THRESHOLDS, type RulesThresholds } from '~/rules/thresholds'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { listMeasurementSessions } from '~/storage/repositories/measurementRepository'
import { getNutritionPlanOn } from '~/storage/repositories/nutritionPlanRepository'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { listActiveSentinelLifts } from '~/storage/repositories/sentinelLiftRepository'
import { listAllStrengthEntriesForLift } from '~/storage/repositories/strengthEntryRepository'
import { listWeeklyReviews } from '~/storage/repositories/weeklyReviewRepository'
import type { CalendarDate } from '~/types/dates'
import { toIsoTimestamp } from '~/types/dates'
import { ok, type Result } from '~/utils/result'
import { type LiftHistory, summariseRecovery, summariseStrength } from './evidenceSummary'
import { getEffectiveWeekStartDay } from './weekBoundaryService'
import { getTrendObservations, todayFor } from './weightService'

/**
 * The impure boundary of the rules engine.
 *
 * Everything that reads storage or the clock happens here; `evaluateWeek` then
 * runs on a plain value. implementation-plan.md §4 sketches this as
 * `rules/inputs/collect.ts`, but the layering guard forbids anything under
 * `rules/` from importing `storage/` — so the collection lives here and only
 * the snapshot TYPE lives under `rules/`. The guard is the stronger authority,
 * and the split it forces is the right one: it is what keeps the engine
 * fixture-testable and deterministic.
 */

export async function collectRulesInput(
  db: SolaDatabase,
  now: Date,
): Promise<Result<RulesInputSnapshot, StorageError>> {
  const reference = todayFor(now)

  const observations = await getTrendObservations(db, reference)
  if (!observations.ok) return observations

  const phase = await getActivePhase(db)
  if (!phase.ok) return phase

  const sessions = await listMeasurementSessions(db)
  if (!sessions.ok) return sessions

  /* Adherence comes from THIS week's review, draft or finished. A draft counts:
     the user answered the adherence step, and the engine should read what they
     said rather than waiting for them to press Finish (D-060 — the evidence
     exists, so the rule may read it). */
  const reviews = await listWeeklyReviews(db)
  if (!reviews.ok) return reviews

  const weekStartDay = await getEffectiveWeekStartDay(db, now)
  if (!weekStartDay.ok) return weekStartDay

  const weekStart = startOfCalendarWeek(reference, weekStartDay.value)
  const thisWeek = reviews.value.find((review) => review.weekStartDate === weekStart)
  const thisWeekAdherence = classifyAdherence(
    thisWeek?.adherence,
    DEFAULT_RULES_THRESHOLDS.adherence,
  )

  /* Every tracked lift's whole history, for the four-week comparison D-066's
     decline rule reads. Retired lifts are excluded: they are no longer part of
     the plan, and a lift the user stopped doing must not read as lost strength. */
  const lifts = await listActiveSentinelLifts(db)
  if (!lifts.ok) return lifts

  const liftHistories: LiftHistory[] = []
  for (const lift of lifts.value) {
    const entries = await listAllStrengthEntriesForLift(db, lift.id)
    if (!entries.ok) return entries
    liftHistories.push({
      liftId: lift.id as string,
      sets: entries.value.map((entry) => ({
        id: entry.id as string,
        liftId: entry.liftId as string,
        date: entry.date,
        loadKg: entry.loadKg as number,
        reps: entry.reps,
      })),
    })
  }

  const nutrition = await nutritionContextOn(db, reference)
  if (!nutrition.ok) return nutrition

  const active = phase.value

  return ok({
    reference,
    evaluatedAt: toIsoTimestamp(now),
    phase:
      active === undefined
        ? undefined
        : {
            type: active.type,
            startDate: active.startDate,
            plannedEndDate: active.plannedEndDate,
            targetWeeklyChangeMinPercent: active.targetWeeklyChangeMinPercent,
            targetWeeklyChangeMaxPercent: active.targetWeeklyChangeMaxPercent,
            weekNumber: phaseWeekNumber(active.startDate, reference),
          },
    weight: calculateWeightTrend(
      observations.value,
      reference,
      DEFAULT_RULES_THRESHOLDS.sufficiency,
    ),
    waist: waistFrom(sessions.value, reference),
    adherence: thisWeekAdherence,
    weeksHoldingSteady: weeksHoldingSteady(reviews.value, weekStart, thisWeekAdherence),
    recovery: summariseRecovery(reviews.value, weekStart),
    strength: summariseStrength(
      liftHistories,
      weekStart,
      weekStartDay.value,
      DEFAULT_RULES_THRESHOLDS,
    ),
    nutrition: nutrition.value,
  })
}

/**
 * Who sets the nutrition numbers on a given day, as the engine sees it (D-096).
 *
 * **The plan in force ON THE REFERENCE DATE, not the newest one.** A review reads
 * a past week, and judging it against targets set afterwards would measure it by a
 * number that did not exist then — the retroactive rewriting D-046 forbids.
 *
 * A project with no plan, or no project at all, yields `authority: 'none'` —
 * which is what a user who never answered the question should be judged as.
 *
 * Split out of the collector rather than inlined: it pushed that function past
 * the complexity the linter allows, and D-056 forbids suppressing that warning in
 * code that decides what the user is told to do.
 */
async function nutritionContextOn(
  db: SolaDatabase,
  reference: CalendarDate,
): Promise<Result<NutritionContext, StorageError>> {
  const none: NutritionContext = { authority: 'none', configuredGoalRatePercent: undefined }

  const project = await getActiveProject(db)
  if (!project.ok) return project
  if (project.value === undefined) return ok(none)

  const plan = await getNutritionPlanOn(db, project.value.id, reference as string)
  if (!plan.ok) return plan
  if (plan.value === undefined) return ok(none)

  return ok({
    authority: authorityOf(plan.value.source),
    configuredGoalRatePercent: plan.value.configuredGoalRatePercent,
  })
}

/**
 * How many consecutive weeks, ending with this one, recorded high adherence.
 *
 * **What "the pattern" means here is deliberately narrow, and the narrowing is
 * the honest part.** D-066 requires the picture to hold for two consecutive
 * weeks before confidence reaches `high`. Only one of the four conditions is
 * stored per week — the adherence tier — so that is what this counts. The other
 * three are evaluated for the current week by `assessConfidence`.
 *
 * Recomputing a past week's weigh-in sufficiency and waist trend from live data
 * was rejected: an edited or deleted entry would retroactively change a week the
 * user has already been given an answer about, which is the failure `phaseSnapshot`
 * exists to prevent (D-046).
 *
 * Returns 0 when this week itself does not qualify — a run has to include the
 * week being judged.
 */
export function weeksHoldingSteady(
  reviews: readonly WeeklyReviewRecord[],
  currentWeekStart: CalendarDate,
  currentAdherence: AdherenceEvidence,
  thresholds: RulesThresholds = DEFAULT_RULES_THRESHOLDS,
): number {
  if (currentAdherence !== 'high') return 0

  const byWeek = new Map(reviews.map((review) => [review.weekStartDate as string, review]))
  let run = 1
  let week = addDays(currentWeekStart, -DAYS_IN_WEEK)

  while (true) {
    const review = byWeek.get(week as string)
    if (review === undefined || review.status !== 'complete') break
    if (classifyAdherence(review.adherence, thresholds.adherence) !== 'high') break
    run += 1
    week = addDays(week, -DAYS_IN_WEEK)
  }

  return run
}

const DAYS_IN_WEEK = 7

/**
 * The two most recent waist medians on or before the reference date.
 *
 * Sessions are read in date order and filtered rather than assumed sorted, so
 * the comparison is always "latest against the one before it" even when several
 * sessions share a date — which the current data model permits.
 */
export function waistFrom(
  sessions: readonly MeasurementSessionRecord[],
  reference: CalendarDate,
): WaistObservation | undefined {
  const withWaist = sessions
    .filter((session) => session.date <= reference && session.sites.waistAtNavel !== undefined)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))

  const latest = withWaist.at(-1)
  if (!latest?.sites.waistAtNavel) return undefined

  const previous = withWaist.at(-2)

  return {
    latestCm: latest.sites.waistAtNavel.medianCm,
    latestDate: latest.date,
    previousCm: previous?.sites.waistAtNavel?.medianCm,
    previousDate: previous?.date,
    latestExceededSpreadThreshold: latest.sites.waistAtNavel.exceedsThreshold,
  }
}

/** Collect and evaluate in one step, for callers that only want the outcome. */
export async function getWeeklyOutcome(
  db: SolaDatabase,
  now: Date,
): Promise<Result<RulesOutcome, StorageError>> {
  const snapshot = await collectRulesInput(db, now)
  if (!snapshot.ok) return snapshot
  return ok(evaluateWeek(snapshot.value))
}
